// engine-exception: a business calendar is edited as a weekly hours grid plus a holiday list spanning three resources (business-calendars, business-hours, holidays) — not one record's form
import { useEffect, useState } from 'react';
import { useCreate, useRemove, useResourceList, useUpdate } from '../../engine/api/hooks';
import { Chip, EmptyState, ErrorBanner, PageHeader, Skeleton } from '../../engine/design/primitives/Misc';
import { Button, IconButton } from '../../engine/design/primitives/Button';
import { Field, Input, Switch } from '../../engine/design/primitives/Input';
import { DatePicker } from '../../engine/design/primitives/DatePicker';
import { formatDate } from '../../lib/format';

// Business calendar editor: per-weekday working windows (any number per day) + holidays.
// Every edit is a resource write (business-hours / holidays CRUD); the calendar's own fields
// (name, timezone, 24×7) PATCH business-calendars.

type Row = Record<string, unknown>;
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export function CalendarEditorScreen() {
  const calendars = useResourceList<Row>('business-calendars', { sort: 'name' }, { limit: 100 });
  const [selected, setSelected] = useState<string | null>(null);
  const cal = calendars.rows.find((c) => c.id === selected) ?? calendars.rows.find((c) => c['is_default']) ?? calendars.rows[0] ?? null;
  const calId = cal ? String(cal['id']) : null;
  const hours = useResourceList<Row>('business-hours', { filter: calId ? [{ field: 'calendar_id', op: 'eq', value: calId }] : undefined, sort: 'weekday' }, { limit: 200, enabled: Boolean(calId) });
  const holidays = useResourceList<Row>('holidays', { filter: calId ? [{ field: 'calendar_id', op: 'eq', value: calId }] : undefined, sort: 'on_date' }, { limit: 200, enabled: Boolean(calId) });
  const updateCal = useUpdate('business-calendars', { toast: { success: 'Calendar saved' } });
  const createCal = useCreate('business-calendars', { toast: { success: 'Calendar created' } });
  const createHours = useCreate('business-hours');
  const updateHours = useUpdate('business-hours');
  const removeHours = useRemove('business-hours');
  const createHoliday = useCreate('holidays');
  const removeHoliday = useRemove('holidays');
  const [head, setHead] = useState({ name: '', timezone: 'UTC', is_24x7: false, is_default: false });
  const [holiday, setHoliday] = useState<{ on_date: string | null; name: string }>({ on_date: null, name: '' });

  useEffect(() => {
    if (cal) setHead({ name: String(cal['name'] ?? ''), timezone: String(cal['timezone'] ?? 'UTC'), is_24x7: Boolean(cal['is_24x7']), is_default: Boolean(cal['is_default']) });
  }, [cal]);

  if (calendars.error) return <ErrorBanner error={calendars.error} />;
  const hh = (v: unknown) => String(v ?? '').slice(0, 5);
  return (
    <div className="hf-page narrow">
      <PageHeader compact title="Business calendars" subtitle="Working hours and holidays that SLA clocks and workflow timeouts count on."
        actions={<Button variant="outline" icon="plus" onClick={() => createCal.mutate({ name: `Calendar ${calendars.rows.length + 1}` }, { onSuccess: (r) => { calendars.refetch(); setSelected(String((r as Row)['id'])); } })}>New calendar</Button>} />
      <div className="hf-chiprow" role="tablist" aria-label="Calendars">
        {calendars.rows.map((c) => <Chip key={String(c['id'])} role="tab" aria-selected={calId === c['id']} active={calId === c['id']} onClick={() => setSelected(String(c['id']))}>{String(c['name'])}{c['is_default'] ? ' · default' : ''}</Chip>)}
      </div>
      {calendars.isLoading ? <Skeleton lines={6} /> : !cal ? <EmptyState title="No calendar" icon="calendar" /> : (
        <div className="hf-stack">
          <div className="hf-form-grid">
            <Field label="Name" id="sd-cal-name"><Input id="sd-cal-name" value={head.name} onChange={(e) => setHead({ ...head, name: e.target.value })} /></Field>
            <Field label="Timezone" id="sd-cal-tz" help="IANA name, e.g. Europe/London"><Input id="sd-cal-tz" value={head.timezone} onChange={(e) => setHead({ ...head, timezone: e.target.value })} /></Field>
            <div className="hf-row" style={{ alignSelf: 'end' }}>
              <Switch label="24 × 7" checked={head.is_24x7} onChange={(e) => setHead({ ...head, is_24x7: e.target.checked })} />
              <Switch label="Default" checked={head.is_default} onChange={(e) => setHead({ ...head, is_default: e.target.checked })} />
              <Button variant="primary" icon="save" busy={updateCal.isPending} onClick={() => updateCal.mutate({ id: calId!, body: head }, { onSuccess: () => calendars.refetch() })}>Save</Button>
            </div>
          </div>
          {!head.is_24x7 && (
            <>
              <h3 className="hf-section-title">Weekly hours</h3>
              <div className="hf-stack" aria-label="Weekly hours">
                {DAYS.map((d, weekday) => {
                  const windows = hours.rows.filter((h) => Number(h['weekday']) === weekday);
                  return (
                    <div key={d} className="hf-row" style={{ alignItems: 'flex-start' }}>
                      <span className="hf-caps" style={{ width: 48, paddingTop: 8 }}>{d}</span>
                      <div className="hf-stack hf-grow" style={{ gap: 'var(--s-1)' }}>
                        {windows.length === 0 && <span className="hf-muted" style={{ paddingTop: 6 }}>closed</span>}
                        {windows.map((w) => (
                          <div key={String(w['id'])} className="hf-row">
                            <Input size="sm" type="time" value={hh(w['start_time'])} aria-label={`${d} start`} onChange={(e) => updateHours.mutate({ id: String(w['id']), body: { start_time: e.target.value } }, { onSuccess: () => hours.refetch() })} />
                            <span className="hf-muted">–</span>
                            <Input size="sm" type="time" value={hh(w['end_time'])} aria-label={`${d} end`} onChange={(e) => updateHours.mutate({ id: String(w['id']), body: { end_time: e.target.value } }, { onSuccess: () => hours.refetch() })} />
                            <IconButton icon="x" label={`Remove ${d} window`} size="sm" onClick={() => removeHours.mutate(String(w['id']), { onSuccess: () => hours.refetch() })} />
                          </div>
                        ))}
                      </div>
                      <Button size="sm" variant="ghost" icon="plus" onClick={() => createHours.mutate({ calendar_id: calId, weekday, start_time: '09:00', end_time: '17:00' }, { onSuccess: () => hours.refetch() })}>Window</Button>
                    </div>
                  );
                })}
              </div>
              <h3 className="hf-section-title">Holidays</h3>
              <div className="hf-row" style={{ flexWrap: 'wrap' }}>
                <DatePicker value={holiday.on_date} onChange={(v) => setHoliday({ ...holiday, on_date: v })} aria-label="Holiday date" />
                <Input size="sm" placeholder="Name" value={holiday.name} onChange={(e) => setHoliday({ ...holiday, name: e.target.value })} aria-label="Holiday name" />
                <Button size="sm" icon="plus" disabled={!holiday.on_date} onClick={() => createHoliday.mutate({ calendar_id: calId, on_date: holiday.on_date, name: holiday.name || undefined }, { onSuccess: () => { holidays.refetch(); setHoliday({ on_date: null, name: '' }); } })}>Add holiday</Button>
              </div>
              <ul className="hf-stack" style={{ listStyle: 'none', padding: 0, margin: 0, gap: 'var(--s-1)' }} aria-label="Holidays">
                {holidays.rows.map((h) => (
                  <li key={String(h['id'])} className="hf-between"><span><span className="hf-num">{formatDate(String(h['on_date']))}</span> <span className="hf-muted">{String(h['name'] ?? '')}</span></span><IconButton icon="x" label="Remove holiday" size="sm" onClick={() => removeHoliday.mutate(String(h['id']), { onSuccess: () => holidays.refetch() })} /></li>
                ))}
                {holidays.rows.length === 0 && <li className="hf-muted">No holidays.</li>}
              </ul>
            </>
          )}
        </div>
      )}
    </div>
  );
}
